import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { api } from "./api/client";
import "./Layout.css";

// Site iskeleti (navbar + sidebar) - eski php-sys/header.php + sidenav.php
const MENU = [
  {
    title: "Teklifler", icon: "fas fa-file-invoice", section: "teklif",
    items: [
      { title: "Pano", to: "/" },
      { title: "Teklif Ekle", to: "/teklif-olustur" },
      { title: "Tüm Teklifler", to: "/tum-teklifler" },
      { title: "Hazırlanan Teklifler", to: "/hazirlananlar" },
      { title: "Onay Bekleyen Teklifler", to: "/onay-bekleyenler" },
      { title: "Onaylanan Teklifler", to: "/onaylananlar" },
      { title: "Tamamlanan Teklifler", to: "/tamamlananlar" },
      { title: "Sevk Edilenler", to: "/sevk-edilenler" },
      { title: "Reddedilen Teklifler", to: "/iptal-edilenler" },
    ],
  },
  {
    title: "Raporlar", icon: "fas fa-chart-bar", section: "reports",
    items: [
      { title: "Satış Raporu", to: "/satis-grafikleri" },
      { title: "Detaylı Satış Raporu", to: "/detayli-teklif-satis-raporu" },
      { title: "Ödeme Takip", to: "/odeme-takip-raporu" },
    ],
  },
  {
    title: "Sözleşmeler", icon: "fas fa-file-contract", section: "sozlesmeler",
    items: [
      { title: "Onaylananlar", to: "/onayli-sozlesmeler" },
    ],
  },
  {
    title: "İş Emri Takvimi", icon: "fas fa-calendar-alt", section: "is_emri_takvimi",
    items: [{ title: "İş Emri Takvimi", to: "/task-table" }],
  },
  {
    title: "İş Emirleri", icon: "fas fa-tasks", section: "isemri",
    items: [
      { title: "İş Emri Ekle", to: "/talep-ekle" },
      { title: "Takvim", to: "/task-table" },
      { title: "Tüm İş Emirleri", to: "/tum-is-emirleri" },
      { title: "Talep İş Emirleri", to: "/talep-is-emirleri" },
      { title: "Taslak İş Emirleri", to: "/taslak-is-emirleri" },
      { title: "Final İş Emirleri", to: "/onayli-is-emirleri", badgeDurum: "Final" },
      { title: "Devam Eden İş Emirleri", to: "/devam-eden-is-emirleri", badgeDurum: "Devam" },
      { title: "Pasif Devam İş Emirleri", to: "/pasif-devam-is-emirleri", badgeDurum: "Pasif Devam" },
      { title: "Tamamlanan İş Emirleri", to: "/tamamlanan-is-emirleri", badgeDurum: "Tamamlandi" },
      { title: "İptal İş Emirleri", to: "/iptal-is-emirleri" },
      { title: "İzinler", to: "/izin-listesi" },
      { title: "Vizeler", to: "/vize-listesi" },
    ],
  },
  {
    title: "Müşteriler", icon: "fas fa-users", section: "musteriler",
    items: [
      { title: "Müşteri Listesi", to: "/musteri-listele" },
    ],
  },
  {
    title: "Makineler", icon: "fas fa-industry", section: "makineler",
    items: [
      { title: "Makine Listesi", to: "/makine-listele" },
      { title: "Lokasyon Güncelle", to: "/makine-lokasyon-guncelle" },
    ],
  },
  {
    title: "Yönetim", icon: "fas fa-user-shield", section: "kullanicilar",
    items: [
      { title: "Yeni arayüz", to: "/yeni" },
      { title: "Kullanıcı Yönetimi", to: "/kullanici-yonetimi" },
    ],
  },
];

const MOBILE_MQ = 1024;

function isMobile() {
  return typeof window !== "undefined" && window.innerWidth <= MOBILE_MQ;
}

function withEski(to) {
  if (!to || to === "/" || to === "/yeni") return "/eski";
  if (to.startsWith("/eski")) return to;
  return `/eski${to.startsWith("/") ? to : `/${to}`}`;
}

function withoutEski(pathname) {
  if (!pathname || pathname === "/eski") return "/";
  if (pathname.startsWith("/eski/")) return pathname.slice("/eski".length);
  return pathname;
}

function pathMatches(pathname, to) {
  const p = pathname.replace(/\/$/, "") || "/";
  const t = to.replace(/\/$/, "") || "/";
  if (t === "/") return p === "/";
  return p === t || p.startsWith(`${t}/`);
}

function findActiveGroup(pathname) {
  // Daha spesifik route önce (Pano /servis her şeyi yemesin)
  let best = null;
  let bestLen = -1;
  for (const g of MENU) {
    for (const it of g.items) {
      if (pathMatches(pathname, it.to) && it.to.length > bestLen) {
        best = g.title;
        bestLen = it.to.length;
      }
    }
  }
  return best;
}

function toggleSidebar() {
  const body = document.body;
  if (isMobile()) {
    body.classList.remove("search-show", "search-gone");
    if (body.classList.contains("sidebar-gone")) {
      body.classList.remove("sidebar-gone");
      body.classList.add("sidebar-show");
    } else {
      body.classList.add("sidebar-gone");
      body.classList.remove("sidebar-show");
    }
  } else {
    body.classList.toggle("sidebar-mini");
  }
}

function closeMobileSidebar() {
  if (!isMobile()) return;
  document.body.classList.add("sidebar-gone");
  document.body.classList.remove("sidebar-show");
}

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userName = user?.full_name || "";
  const [sections, setSections] = useState(null);
  const [unreadBadges, setUnreadBadges] = useState({});

  const here = withoutEski(location.pathname);
  const yonetici = user?.role === "admin" || user?.role === "superadmin";
  const activeGroupTitle = findActiveGroup(here);

  useEffect(() => {
    if (user && !yonetici) {
      navigate(`${here}${location.search}`, { replace: true });
    }
  }, [user, yonetici, here, location.search, navigate]);
  const [openGroup, setOpenGroup] = useState(activeGroupTitle);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/me/menu-sections")
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setSections(list);
        try {
          const calViewer = list.includes("is_emri_takvimi") && !list.includes("isemri");
          sessionStorage.setItem("ct_cal_ro", calViewer ? "1" : "0");
          sessionStorage.setItem("ct_cal_only", calViewer ? "1" : "0");
        } catch (e) { /* ignore */ }
      })
      .catch(() => {
        // Fail-closed: yetki gelmezse menüyü açma (ilk boyamada full flash olmasın)
        if (!cancelled) setSections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // PHP sidenav badge — okunmamış not/rapor sayıları
  useEffect(() => {
    if (!user?.id) {
      setUnreadBadges({});
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      api
        .get("/is-emirleri/unread-badges")
        .then((res) => {
          if (!cancelled && res.data && typeof res.data === "object") {
            setUnreadBadges(res.data);
          }
        })
        .catch(() => {
          if (!cancelled) setUnreadBadges({});
        });
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user?.id, location.pathname]);

  const calendarViewer = Array.isArray(sections)
    && sections.includes("is_emri_takvimi")
    && !sections.includes("isemri");
  useEffect(() => {
    if (!calendarViewer) return;
    const bare = (here.replace(/^\/eski/, "") || "/").replace(/\/$/, "") || "/";
    if (bare === "/task-table" || bare === "/tum-is-emirleri") return;
    navigate("/task-table", { replace: true });
  }, [calendarViewer, here, navigate]);

  const visibleMenuBase = MENU.filter((g) => {
    if (sections == null) return false; // yetki yüklenene kadar boş
    if (g.section === "is_emri_takvimi" && !(sections.includes("is_emri_takvimi") && !sections.includes("isemri"))) return false;
    if (user?.role === "admin" || user?.role === "superadmin") return true;
    return sections.includes(g.section);
  });
  const visibleMenu = calendarViewer && !visibleMenuBase.some((g) => g.section === "isemri")
    ? [
        ...visibleMenuBase,
        {
          title: "İş Emirleri",
          icon: "fas fa-tasks",
          section: "isemri",
          items: [{ title: "Tüm İş Emirleri", to: "/tum-is-emirleri" }],
        },
      ]
    : visibleMenuBase;

  // Mobilde başlangıçta kapalı sidebar
  useEffect(() => {
    const apply = () => {
      const body = document.body;
      if (isMobile()) {
        body.classList.add("sidebar-gone");
        body.classList.remove("sidebar-show", "sidebar-mini");
      } else {
        body.classList.remove("sidebar-gone", "sidebar-show");
      }
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  // Route değişince aktif grubu aç + mobilde menüyü kapat
  useEffect(() => {
    if (activeGroupTitle) setOpenGroup(activeGroupTitle);
    closeMobileSidebar();
  }, [location.pathname, activeGroupTitle]);

  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div id="app">
      <div className="main-wrapper">
        <button
          type="button"
          className="ct-sidebar-backdrop"
          aria-label="Menüyü kapat"
          onClick={closeMobileSidebar}
        />
        <div className="navbar-bg"></div>
        <nav className="navbar navbar-expand-lg main-navbar">
          <form className="form-inline mr-auto">
            <ul className="navbar-nav mr-3">
              <li>
                <a
                  href="#"
                  data-toggle="sidebar"
                  id="sidebarButton"
                  className="nav-link nav-link-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleSidebar();
                  }}
                >
                  <i className="fas fa-bars"></i>
                </a>
              </li>
            </ul>
          </form>
          <ul className="navbar-nav navbar-right">
            <li className="dropdown">
              <a
                href="#"
                data-toggle="dropdown"
                className="nav-link dropdown-toggle nav-link-lg nav-link-user"
              >
                <img
                  alt=""
                  src="/assets/img/avatar/avatar-1.png"
                  className="rounded-circle mr-1"
                />
                <div className="d-inline-block ct-user-name">{userName || "Merhaba"}</div>
              </a>
              <div className="dropdown-menu dropdown-menu-right">
                <a
                  href="#"
                  className="dropdown-item has-icon text-danger"
                  onClick={handleLogout}
                >
                  <i className="fas fa-sign-out-alt"></i> Çıkış
                </a>
              </div>
            </li>
          </ul>
        </nav>

        <div className="main-sidebar">
          <aside id="sidebar-wrapper">
            <div className="sidebar-brand">
              <Link to="/eski" onClick={closeMobileSidebar}>
                <img src="/assets/img/menu_logo.png" width="140" alt="Coiltech" />
              </Link>
            </div>
            <div className="sidebar-brand sidebar-brand-sm">
              <Link to="/eski" onClick={closeMobileSidebar}>
                <img src="/assets/img/menu_kck_logo.png" width="40" alt="Coiltech" />
              </Link>
            </div>
            <ul className="sidebar-menu">
              {visibleMenu.map((group) => {
                const isOpen = calendarViewer || openGroup === group.title;
                const groupBadge = group.items.reduce((sum, item) => {
                  const n = item.badgeDurum && unreadBadges[item.badgeDurum]
                    ? Number(unreadBadges[item.badgeDurum])
                    : 0;
                  return sum + (Number.isFinite(n) ? n : 0);
                }, 0);
                return (
                  <li
                    key={group.title}
                    className={`nav-item dropdown${isOpen ? " active" : ""}`}
                  >
                    <a
                      href="#"
                      className="nav-link has-dropdown"
                      onClick={(e) => {
                        e.preventDefault();
                        if (calendarViewer) return;
                        setOpenGroup(isOpen ? null : group.title);
                      }}
                    >
                      <i className={group.icon}></i> <span>{group.title}</span>
                      {groupBadge > 0 && <span className="ct-menu-badge">{groupBadge}</span>}
                    </a>
                    <ul
                      className="dropdown-menu"
                      style={{ display: isOpen ? "block" : "none" }}
                    >
                      {group.items.map((item) => {
                        const badgeN =
                          item.badgeDurum && unreadBadges[item.badgeDurum]
                            ? Number(unreadBadges[item.badgeDurum])
                            : 0;
                        return (
                        <li
                          key={item.to}
                          className={item.to !== "/yeni" && pathMatches(here, item.to) ? "active" : ""}
                        >
                          <Link
                            className="nav-link"
                            to={item.to === "/yeni" ? here : withEski(item.to)}
                            onClick={closeMobileSidebar}
                          >
                            <span>{item.title}</span>
                            {badgeN > 0 && (
                              <span className="ct-menu-badge">{badgeN}</span>
                            )}
                          </Link>
                        </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>

        <div
          className="main-content"
          onClickCapture={(event) => {
            const anchor = event.target.closest && event.target.closest("a[href]");
            if (!anchor || anchor.target === "_blank") return;
            const href = anchor.getAttribute("href") || "";
            if (!href.startsWith("/") || href.startsWith("//") || href.startsWith("/eski") || href.startsWith("/login")) return;
            event.preventDefault();
            event.stopPropagation();
            navigate(href === "/" ? "/eski" : `/eski${href}`);
          }}
        >
          <section className="section">
            <div className="row">
              <div className="col-12">
                <Outlet />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Layout;
